import {
    Text,
    HStack,
    PinInput,
    PinInputField,
    Flex,
    Divider,
    Button,
} from '@chakra-ui/react'
import {
    useGetEmailVerificationCodeMutation,
    useSendEmailVerificationCodeMutation,
} from '@core/app/api/authApi'
import {
    addStepCount,
    allStepsCount,
    email,
    nextStep,
    removeStepCount,
    setEmailVerificationId,
    stepCount,
} from '@core/app/slices/auth'
import { useAppDispatch, useAppSelector } from '@core/utils/hooks/hook'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export const EmailVerificationStep = () => {
    const dispatch = useAppDispatch()
    const emailValue = useAppSelector(email)
    const stepsCount = useAppSelector(allStepsCount)
    const stepNumber = useAppSelector(stepCount)
    const [getEmailVerificationCode] = useGetEmailVerificationCodeMutation()
    const [sendEmailVerificationCode] = useSendEmailVerificationCodeMutation()
    const [count, setCount] = useState<number>(300)
    const [err, setErr] = useState<string>('')
    const { t } = useTranslation()

    const getEmailVerificationHandler = async () => {
        try {
            await getEmailVerificationCode({
                data: {
                    email: emailValue,
                },
            }).unwrap()
            setCount(300)
        } catch (error) {
            console.log(error)
        }
    }

    const sendEmailVerificationCodeHandler = async (code: string) => {
        try {
            const response = await sendEmailVerificationCode({
                data: {
                    code,
                },
            }).unwrap()

            dispatch(setEmailVerificationId(response.data.id))
            dispatch(addStepCount())
            dispatch(nextStep('personalDataExtanded'))
        } catch (error: any) {
            setErr(error.data?.data?.errors?.code[0] || 'unknown error')

            setTimeout(() => {
                dispatch(removeStepCount())
                dispatch(nextStep('createProfile'))
            }, 1500)
        }
    }

    useEffect(() => {
        if (count === 0) return

        let interval = setInterval(() => {
            setCount((prev) => prev - 1)
        }, 1000)
        return () => clearInterval(interval)
    }, [count])

    return (
        <Flex flexDirection={'column'}>
            <Text fontSize={'xs'} color={'text.thirdly'} mb={1}>
                {t('6593')} {stepNumber} {t('1165')} {stepsCount}
            </Text>
            <Text
                fontSize={'2xl'}
                color={'text.main'}
                fontWeight={'semibold'}
                mb={4}
            >
                {t('6624')}
            </Text>
            <Text color={'text.secondary'} fontWeight={'medium'}>
                {t('6588')}
            </Text>
            <Text variant={'noMargins'} color={'text.link'} fontWeight={'bold'}>
                {emailValue}
            </Text>
            <HStack mt={10}>
                <PinInput
                    otp
                    placeholder={''}
                    size={'lg'}
                    autoFocus={true}
                    type={'alphanumeric'}
                    isInvalid={err.length > 0}
                    focusBorderColor={'state.primary'}
                    errorBorderColor={'formElements.borderError'}
                    onComplete={(code) => {
                        sendEmailVerificationCodeHandler(code)
                    }}
                    onChange={() => setErr('')}
                >
                    <PinInputField w={'51px'} h={'60px'} />
                    <PinInputField w={'51px'} h={'60px'} />
                    <PinInputField w={'51px'} h={'60px'} />
                    <PinInputField w={'51px'} h={'60px'} />
                </PinInput>
            </HStack>
            {err && (
                <Text fontSize={'xs'} color={'text.error'} mt={1}>
                    {err}
                </Text>
            )}
            <Text fontSize={'sm'} color={'text.thirdly'} mt={2}>
                {t('6589')}{' '}
                <Text as="b" color={'text.secondary'}>
                    {count}
                </Text>{' '}
                {t('6590')}
            </Text>
            <Divider mt={'112px'} />
            <Text
                fontSize={'sm'}
                fontWeight={'medium'}
                color={'text.thirdly'}
                mt={6}
            >
                {t('6591')}.
            </Text>
            {count === 0 ? (
                <Button
                    variant={'link'}
                    color={'text.link'}
                    mt={3}
                    alignSelf={'start'}
                    onClick={() => getEmailVerificationHandler()}
                >
                    {t('6592')}
                </Button>
            ) : null}
        </Flex>
    )
}
